import {Box, Typography} from '@mui/material';
import {useSecrets} from '@wandb/weave/common/hooks/useSecrets';
import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';

// Shared typography style
export const sharedTypographyStyle = {
  fontSize: '16px',
  fontFamily: 'Source Sans Pro',
};

// Header component
export const ProviderDrawerHeader: React.FC<{
  title: string;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onClose: () => void;
  showBackButton?: boolean;
  onBack?: () => void;
}> = ({
  title,
  isFullscreen,
  onToggleFullscreen,
  onClose,
  showBackButton,
  onBack,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pl: 2,
        pr: 1,
        height: '44px',
        minHeight: '44px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        {showBackButton && (
          <Button onClick={onBack} variant="ghost" icon="back" tooltip="Back" />
        )}
        <Typography
          variant="h6"
          sx={{...sharedTypographyStyle, fontSize: '20px', fontWeight: 600}}>
          {title}
        </Typography>
      </Box>
      <Box sx={{display: 'flex', gap: 1}}>
        <Button
          onClick={onToggleFullscreen}
          variant="ghost"
          icon={isFullscreen ? 'minimize-mode' : 'full-screen-mode-expand'}
          tooltip={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        />
        <Button
          onClick={onClose}
          variant="ghost"
          icon="close"
          tooltip="Close"
        />
      </Box>
    </Box>
  );
};

// Headers component
export const HeadersInput: React.FC<{
  headers: Array<[string, string]>;
  onHeadersChange: (headers: Array<[string, string]>) => void;
}> = ({headers, onHeadersChange}) => {
  return (
    <Box>
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Typography sx={{...sharedTypographyStyle, fontWeight: '600'}}>
          Headers{' '}
          <Typography
            component="span"
            sx={{
              ...sharedTypographyStyle,
              fontWeight: '400',
              fontSize: '14px',
              color: 'text.secondary',
            }}>
            (Optional)
          </Typography>
        </Typography>
        <Button
          variant="secondary"
          size="small"
          icon="add-new"
          onClick={() =>
            onHeadersChange(headers ? [...headers, ['', '']] : [['', '']])
          }>
          Add a header
        </Button>
      </Box>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
        {headers?.map(([key, value], index) => (
          <Box key={index} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <TextField
              placeholder="Enter header key..."
              value={key}
              onChange={val =>
                onHeadersChange(
                  headers.map(([k, v], i) => (i === index ? [val, v] : [k, v]))
                )
              }
            />
            <TextField
              placeholder="Enter header value..."
              value={value}
              onChange={val =>
                onHeadersChange(
                  headers.map(([k, v], i) => (i === index ? [k, val] : [k, v]))
                )
              }
            />
            <Button
              variant="ghost"
              icon="delete"
              onClick={() => {
                const newHeaders = [...headers];
                newHeaders.splice(index, 1);
                onHeadersChange(newHeaders);
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Provider name component
export const ProviderNameInput: React.FC<{
  name: string;
  onNameChange: (value: string) => void;
  nameIsUnique: boolean;
  providers: string[];
}> = ({name, onNameChange, nameIsUnique, providers}) => {
  const nameNoSpaces = name.includes(' ') === false;

  return (
    <Box>
      <Typography sx={{...sharedTypographyStyle, mb: 1, fontWeight: '600'}}>
        Provider name
      </Typography>
      <TextField
        placeholder="Enter provider name..."
        value={name}
        onChange={value => onNameChange(value.replace(/\s/g, '_'))}
        errorState={!nameIsUnique || !nameNoSpaces || name.length > 128}
      />
      {!nameIsUnique && (
        <Typography
          variant="caption"
          color="error"
          sx={{...sharedTypographyStyle, mt: 0.5}}>
          Provider with this name already exists
        </Typography>
      )}
      {!nameNoSpaces && (
        <Typography
          variant="caption"
          color="error"
          sx={{...sharedTypographyStyle, mt: 0.5}}>
          Name cannot contain spaces
        </Typography>
      )}
      {name.length > 128 && (
        <Typography
          variant="caption"
          color="error"
          sx={{...sharedTypographyStyle, mt: 0.5}}>
          Name must be less than 128 characters
        </Typography>
      )}
    </Box>
  );
};

// API Key Input component
type SecretOption = {
  label: string;
  value: string;
};

export const ApiKeyInput: React.FC<{
  apiKey: string;
  onApiKeyChange: (value: string) => void;
  projectId: string;
  entityName: string;
  onCreateSecret?: () => void;
  secretsRefetchRef?: React.MutableRefObject<(() => void) | null>;
}> = ({
  apiKey,
  onApiKeyChange,
  projectId,
  entityName,
  onCreateSecret,
  secretsRefetchRef,
}) => {
  const {loading, secrets, refetch} = useSecrets({entityName});

  // Store refetch in parent's ref
  if (secretsRefetchRef) {
    secretsRefetchRef.current = refetch;
  }

  const options: SecretOption[] = secrets.map(secretName => ({
    label: secretName,
    value: secretName,
  }));

  const getPlaceholderText = () => {
    if (loading) {
      return 'Loading secrets...';
    }
    if (secrets.length === 0) {
      return 'No team secrets available';
    }
    return 'Select secret';
  };

  const selectedOption: SecretOption | null =
    loading || !apiKey
      ? null
      : options.find(option => option.value === apiKey) || null;

  const handleChange = (selected: SecretOption | null) => {
    onApiKeyChange(selected?.value || '');
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}>
        <Typography
          sx={{
            ...sharedTypographyStyle,
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
          }}>
          API key
        </Typography>
        <Button
          variant="secondary"
          size="small"
          icon="add-new"
          onClick={onCreateSecret}>
          Create secret
        </Button>
      </Box>
      <Typography
        sx={{
          ...sharedTypographyStyle,
          color: 'text.secondary',
          fontSize: '0.875rem',
          mb: 1,
        }}>
        Team secrets can be managed in your team settings by team admins.
      </Typography>
      <Select<SecretOption, false>
        options={options}
        value={selectedOption}
        onChange={handleChange}
        isDisabled={loading || secrets.length === 0}
        placeholder={
          <Tailwind>
            <span className="text-sm text-moon-500">
              {getPlaceholderText()}
            </span>
          </Tailwind>
        }
        isClearable={true}
      />
    </Box>
  );
};

// Base URL Input component
export const BaseUrlInput: React.FC<{
  baseUrl: string;
  onBaseUrlChange: (value: string) => void;
}> = ({baseUrl, onBaseUrlChange}) => {
  return (
    <Box>
      <Typography sx={{...sharedTypographyStyle, fontWeight: '600'}}>
        Base URL
      </Typography>
      <Typography
        sx={{
          ...sharedTypographyStyle,
          color: 'text.secondary',
          fontSize: '0.875rem',
          mb: 1,
        }}>
        For example: https://api.yourendpoint.com/v1
      </Typography>
      <TextField
        placeholder="Enter base URL..."
        value={baseUrl}
        onChange={value => onBaseUrlChange(value.replace(/\s/g, ''))}
      />
    </Box>
  );
};

// Whole models section component
export const ModelsSection: React.FC<{
  modelNames: string[];
  maxTokens: number[];
  providerName: string;
  onAddModel: () => void;
  onModelChange: (value: string, index: number) => void;
  onMaxTokensChange: (value: number, index: number) => void;
  onDeleteModel: (index: number) => void;
}> = ({
  modelNames,
  maxTokens,
  providerName,
  onAddModel,
  onModelChange,
  onMaxTokensChange,
  onDeleteModel,
}) => {
  return (
    <Box>
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Typography
          sx={{
            ...sharedTypographyStyle,
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}>
          Models
          <Tooltip
            trigger={
              <Icon name="info" width={16} height={16} color="text-muted" />
            }
            content="Models are the specific AI model names you want to use from the provider. You can add multiple models to the same provider."
          />
        </Typography>
        <Button
          variant="secondary"
          size="small"
          icon="add-new"
          onClick={onAddModel}>
          Add a model
        </Button>
      </Box>
      {modelNames.map((model, index) => (
        <ModelInput
          key={index}
          model={model}
          maxToken={maxTokens[index]}
          providerName={providerName}
          index={index}
          onModelChange={onModelChange}
          onMaxTokensChange={onMaxTokensChange}
          onDelete={onDeleteModel}
          modelNames={modelNames}
        />
      ))}
    </Box>
  );
};

// Model component
export const ModelInput: React.FC<{
  model: string;
  maxToken: number;
  providerName: string;
  index: number;
  onModelChange: (value: string, index: number) => void;
  onMaxTokensChange: (value: number, index: number) => void;
  onDelete: (index: number) => void;
  modelNames: string[];
}> = ({
  model,
  maxToken,
  providerName,
  index,
  onModelChange,
  onMaxTokensChange,
  onDelete,
  modelNames,
}) => {
  return (
    <Box>
      <Box
        sx={{
          mt: index > 0 ? 2 : 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
        }}>
        <Box sx={{width: '75%'}}>
          <TextField
            placeholder="Enter model name..."
            value={model}
            onChange={value => {
              const newValue = value.replace(/\s/g, '');
              onModelChange(newValue, index);
            }}
            errorState={
              providerName.length + model.length > 127 ||
              modelNames.some((m, i) => i !== index && m === model)
            }
          />
        </Box>
        <Box sx={{width: '25%'}}>
          <TextField
            placeholder="Max tokens..."
            type="number"
            value={String(maxToken || '')}
            onChange={value => {
              const newValue = value.replace(/[^0-9]/g, '');
              const numericValue = newValue ? parseInt(newValue, 10) : 0;
              onMaxTokensChange(numericValue, index);
            }}
          />
        </Box>
        <Button variant="ghost" icon="delete" onClick={() => onDelete(index)} />
      </Box>
      {/* Error messages */}
      {providerName.length + model.length > 127 && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            ...sharedTypographyStyle,
            mt: 0.5,
            display: 'block',
          }}>
          {'<Provider>/<Model> cannot be more than 128 characters'}
        </Typography>
      )}
      {modelNames.some((m, i) => i !== index && m === model) && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            ...sharedTypographyStyle,
            mt: 0.5,
            display: 'block',
          }}>
          Model name must be unique
        </Typography>
      )}
    </Box>
  );
};
